import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import ExpandDiv from "../common/ExpandDiv";
import {
  FormInput,
  FormSelect,
  FormCheckbox,
  FormTextArea,
  FormRadioButton,
  FieldTable,
  ToggleSwitch,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function INEFPSD(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const preSignForm = () => {
    const obj = { ...data };

    const selfHarmDetails = JSON.parse(obj.SelfHarmDetails["@value"]);

    if (selfHarmDetails)
      obj.SelfHarmDetails["@value"] = JSON.stringify(
        selfHarmDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const harmingOthersDetails = JSON.parse(obj.HarmingOthersDetails["@value"]);

    if (harmingOthersDetails)
      obj.HarmingOthersDetails["@value"] = JSON.stringify(
        harmingOthersDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const educationDetails = JSON.parse(obj.EducationDetails["@value"]);

    if (educationDetails)
      obj.EducationDetails["@value"] = JSON.stringify(
        educationDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const employmentDetails = JSON.parse(obj.EmploymentDetails["@value"]);

    if (employmentDetails)
      obj.EmploymentDetails["@value"] = JSON.stringify(
        employmentDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const substanceUseHistory = JSON.parse(obj.SubstanceUseHistory["@value"]);

    if (substanceUseHistory)
      obj.SubstanceUseHistory["@value"] = JSON.stringify(
        substanceUseHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const traumaHistory = JSON.parse(obj.TraumaHistory["@value"]);

    if (traumaHistory)
      obj.TraumaHistory["@value"] = JSON.stringify(
        traumaHistory.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const symptoms = JSON.parse(obj.Symptoms["@value"]);

    if (symptoms)
      obj.Symptoms["@value"] = JSON.stringify(
        symptoms.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const diagnosisDetails = JSON.parse(obj.DiagnosisDetails["@value"]);

    if (diagnosisDetails)
      obj.DiagnosisDetails["@value"] = JSON.stringify(
        diagnosisDetails.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    const treatmentRecommendations = JSON.parse(
      obj.TreatmentRecommendations["@value"]
    );

    if (treatmentRecommendations)
      obj.TreatmentRecommendations["@value"] = JSON.stringify(
        treatmentRecommendations.map((item) => {
          return { ...item, IsEditable: false };
        })
      );

    setData(obj);
  };

  const divisionOptions = [
    { _id: "Fort Wayne", name: "Fort Wayne" },
    { _id: "Indianapolis", name: "Indianapolis" },
    { _id: "Merrillville", name: "Merrillville" },
    { _id: "Mishawaka", name: "Mishawaka" },
    { _id: "Southern Indiana", name: "Southern Indiana" },
  ];

  const countyOptions = [
    { _id: "Allen", name: "Allen" },
    { _id: "Benton", name: "Benton" },
    { _id: "Clark", name: "Clark" },
    { _id: "Dearborn", name: "Dearborn" },
    { _id: "Decatur", name: "Decatur" },
    { _id: "DeKalb", name: "DeKalb" },
    { _id: "Elkhart", name: "Elkhart" },
    { _id: "Floyd", name: "Floyd" },
    { _id: "Harrison", name: "Harrison" },
    { _id: "Jasper", name: "Jasper" },
    { _id: "Jefferson", name: "Jefferson" },
    { _id: "Kosciusko", name: "Kosciusko" },
    { _id: "LaGrange", name: "LaGrange" },
    { _id: "Lake", name: "Lake" },
    { _id: "LaPorte", name: "LaPorte" },
    { _id: "Marion", name: "Marion" },
    { _id: "Marshall", name: "Marshall" },
    { _id: "Newton", name: "Newton" },
    { _id: "Noble", name: "Noble" },
    { _id: "Ohio", name: "Ohio" },
    { _id: "Porter", name: "Porter" },
    { _id: "Pulaski", name: "Pulaski" },
    { _id: "Ripley", name: "Ripley" },
    { _id: "Scott", name: "Scott" },
    { _id: "St. Joseph", name: "St. Joseph" },
    { _id: "Starke", name: "Starke" },
    { _id: "Steuben", name: "Steuben" },
    { _id: "Switzerland", name: "Switzerland" },
    { _id: "Washington", name: "Washington" },
    { _id: "Whitley", name: "Whitley" },
    { _id: "Other", name: "Other" },
  ];

  const sexAtBirthOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Intersex", name: "Intersex" },
    { _id: "Sex not listed", name: "Sex not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Non-binary", name: "Non-binary" },
    { _id: "Agender", name: "Agender" },
    { _id: "Transgender (FtM)", name: "Transgender (FtM)" },
    { _id: "Transgender (MtF)", name: "Transgender (MtF)" },
    { _id: "Identity not listed", name: "Identity not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pronounOptions = [
    { _id: "She/Her/Hers", name: "She/Her/Hers" },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Prefer to self-describe", name: "Prefer to self-describe" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const psdTypeOptions = [
    { _id: "Initial", name: "Initial" },
    { _id: "Review/Update", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoUnknownOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const traumaTypeOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const symptomOptions = [
    {
      _id: "Symptoms of Trauma and Other Stressor-Related Disorders",
      name: "Symptoms of Trauma and Other Stressor-Related Disorders",
    },
    { _id: "Symptoms of Anxiety", name: "Symptoms of Anxiety" },
    { _id: "Symptoms of Depression", name: "Symptoms of Depression" },
    { _id: "Symptoms of ADHD", name: "Symptoms of ADHD" },
    { _id: "Symptoms of SUD", name: "Symptoms of SUD" },
    {
      _id: "Symptoms of Disruptive Mood Dysregulation Disorder",
      name: "Symptoms of Disruptive Mood Dysregulation Disorder",
    },
    {
      _id: "Symptoms of Conduct Disorder",
      name: "Symptoms of Conduct Disorder",
    },
    {
      _id: "Symptoms of Oppositional Defiance Disorder",
      name: "Symptoms of Oppositional Defiance Disorder",
    },
    { _id: "Symptoms of OCD", name: "Symptoms of OCD" },
    { _id: "Symptoms of Panic Disorder", name: "Symptoms of Panic Disorder" },
    { _id: "Symptoms of Bipolar", name: "Symptoms of Bipolar" },
    {
      _id: "Symptoms of a Personality Disorder",
      name: "Symptoms of a Personality Disorder",
    },
    { _id: "Other Symptoms", name: "Other Symptoms" },
  ];

  const legalHistoryOptions = [
    {
      _id: "Yes",
      name: "Yes — Family has a legal history",
    },
    {
      _id: "No",
      name: "No — Family does not have a legal history",
    },
  ];

  const completedOptions = [
    { _id: "Completed", name: "Completed" },
    { _id: "Not Completed", name: "Not Completed" },
  ];

  const diagnosisOptions = [
    {
      _id: "V60.9 (Z59.9) Unspecified Housing or Economic Problem",
      name: "V60.9 (Z59.9) Unspecified Housing or Economic Problem",
    },
    {
      _id: "V61.29 (Z62.898) Child Affected by Parental Relationship Distress",
      name: "V61.29 (Z62.898) Child Affected by Parental Relationship Distress",
    },
    {
      _id: "V62.29 (Z56.9) Other Problem Related to Employment",
      name: "V62.29 (Z56.9) Other Problem Related to Employment",
    },
    {
      _id: "V62.3 (Z55.9) Academic or Educational Problem",
      name: "V62.3 (Z55.9) Academic or Educational Problem",
    },
    {
      _id: "V62.5 (Z65.3) Problems Related to Other Legal Circumstance",
      name: "V62.5 (Z65.3) Problems Related to Other Legal Circumstance",
    },
    {
      _id: "V62.82 (Z63.4) Uncomplicated Bereavement",
      name: "V62.82 (Z63.4) Uncomplicated Bereavement",
    },
    {
      _id: "V62.89 (Z65.8) Other Problem Related to Psychosocial Circumstances",
      name: "V62.89 (Z65.8) Other Problem Related to Psychosocial Circumstances",
    },
    {
      _id: "V62.89 (R41.83) Borderline Intellectual Functioning",
      name: "V62.89 (R41.83) Borderline Intellectual Functioning",
    },
    {
      _id: "V62.9 (Z65.9) Unspecified Problem Related to Unspecified Psychosocial Circumstances",
      name: "V62.9 (Z65.9) Unspecified Problem Related to Unspecified Psychosocial Circumstances",
    },
    {
      _id: "V62.9 (Z60.9) Unspecified Problem Related to Social Environment",
      name: "V62.9 (Z60.9) Unspecified Problem Related to Social Environment",
    },
    {
      _id: "V71.09 (Z03.89)	No Diagnosis or Condition",
      name: "V71.09 (Z03.89)	No Diagnosis or Condition",
    },
    {
      _id: "Z55.9 Problem related to education and literacy, unspecified",
      name: "Z55.9 Problem related to education and literacy, unspecified",
    },
    {
      _id: "Z56.7 Other and unspecified problems related to employment",
      name: "Z56.7 Other and unspecified problems related to employment",
    },
    { _id: "Z59.1 Inadequate housing", name: "Z59.1 Inadequate housing" },
    {
      _id: "Z59.9 Problem related to housing and economic circumstances, unspecified",
      name: "Z59.9 Problem related to housing and economic circumstances, unspecified",
    },
    {
      _id: "Z60.8 Other problems related to social environment",
      name: "Z60.8 Other problems related to social environment",
    },
    {
      _id: "Z60.9 Problem related to social environment, unspecified",
      name: "Z60.9 Problem related to social environment, unspecified",
    },
    {
      _id: "995.54 (T74.12XA) Child Physical Abuse, Confirmed",
      name: "995.54 (T74.12XA) Child Physical Abuse, Confirmed",
    },
    {
      _id: "995.54 (T76.12XA) Child Physical Abuse, Suspected",
      name: "995.54 (T76.12XA) Child Physical Abuse, Suspected",
    },
    {
      _id: "995.52 (T74.02XA) Child Neglect, Confirmed",
      name: "995.52 (T74.02XA) Child Neglect, Confirmed",
    },
    {
      _id: "995.52 (T76.02XA) Child Neglect, Suspected",
      name: "995.52 (T76.02XA) Child Neglect, Suspected",
    },
    {
      _id: "995.81 (T74.11XA) Spouse or Partner Violence, Physical, Confirmed",
      name: "995.81 (T74.11XA) Spouse or Partner Violence, Physical, Confirmed",
    },
    {
      _id: "995.81 (T76.11XA) Spouse or Partner Violence, Physical, Suspected",
      name: "995.81 (T76.11XA) Spouse or Partner Violence, Physical, Suspected",
    },
    { _id: "Other", name: "Other" },
  ];

  const serviceTypeOptions = [
    { _id: "Concrete Services", name: "Concrete Services" },
    { _id: "Individual Therapy", name: "Individual Therapy" },
    {
      _id: "Family Preservation Case Management",
      name: "Family Preservation Case Management",
    },
    { _id: "Family Therapy", name: "Family Therapy" },
  ];

  const getSelfHarmRisk = (index) => {
    const risks = [];
    const shData = JSON.parse(data.SelfHarmDetails["@value"])[index];

    if (shData.SelfAbusive === "true")
      risks.push("Self-injurious or self-abusive");
    if (shData.SuicidalThreats === "true")
      risks.push("Suicidal threats/gestures");
    if (shData.SuicideAttempt === "true") risks.push("Suicide attempt");

    if (risks.length > 0) return risks.join(", ");
    else return "None reported";
  };

  const getHarmOthersRisk = (index) => {
    const risks = [];
    const hoData = JSON.parse(data.HarmingOthersDetails["@value"])[index];

    if (hoData.Thoughts === "true")
      risks.push("Thoughts of intentionally harming/endangering others");
    if (hoData.Threats === "true")
      risks.push("Threats to intentionally harm/endanger others");
    if (hoData.HarmOthers === "true")
      risks.push("Acts of intentionally harming/endangering others");
    if (hoData.HarmAnimals === "true")
      risks.push("Acts of intentionally harming/endangering animals");

    if (risks.length > 0) return risks.join(", ");
    else return "None reported";
  };

  const buildClinicalNarrativeSummary = () => {
    if (!formDisabled) {
      let summary = "";

      if (data.ReferralReason["@value"] && data.ReferralReason["@value"] !== "")
        summary += `Reason for Referral: ${data.ReferralReason["@value"]}\r\n\r\n`;

      const symptoms = JSON.parse(data.Symptoms["@value"]).filter(
        (item) => !item.IsEditable
      );

      if (symptoms.length > 0)
        summary += `Symptoms:\r\n${symptoms
          .map(
            (item) =>
              `${
                item.FamilyMember && item.FamilyMember !== ""
                  ? `${item.FamilyMember}: `
                  : ""
              }${
                item.Symptoms
                  ? `${item.Symptoms.join(", ")}${
                      item.OtherSymptomDetails &&
                      item.OtherSymptomDetails !== ""
                        ? `, ${item.OtherSymptomDetails}`
                        : ""
                    }`
                  : ""
              }${
                item.Description && item.Description !== ""
                  ? `, Description: ${item.Description}`
                  : ""
              }`
          )
          .join(". ")}\r\n\r\n`;

      if (
        data.MentalHealthTreatment["@value"] &&
        data.MentalHealthTreatment["@value"] !== ""
      )
        summary += `Mental Health Treatment: ${data.MentalHealthTreatment["@value"]}\r\n\r\n`;

      // Trauma History
      let traumaSummary = "";

      const traumas = JSON.parse(data.TraumaHistory["@value"]).filter(
        (item) => !item.IsEditable
      );

      if (traumas.length > 0)
        traumaSummary += `${traumas
          .filter((item) => item.Types !== undefined)
          .map(
            (item) =>
              `${
                item.FamilyMember && item.FamilyMember !== ""
                  ? `${item.FamilyMember}: `
                  : ""
              }${
                item.Types ? item.Types.join(", ") : ""
              }${`, Comments: ${item.Comments}`}`
          )
          .join(". ")}; `;

      if (
        data.FamilyChanges["@value"] === "Yes" &&
        data.FamilyChangesDetails["@value"] &&
        data.FamilyChangesDetails["@value"] === ""
      )
        traumaSummary += `Changes/transitions in family: ${data.FamilyChangesDetails["@value"]}; `;

      if (
        data.RecentGrief["@value"] === "Yes" &&
        data.RecentGriefDetails["@value"] &&
        data.RecentGriefDetails["@value"] === ""
      )
        traumaSummary += `Recent or significant deaths/grief issues: ${data.RecentGriefDetails["@value"]}; `;

      if (traumaSummary !== "")
        summary += `Trauma History:\r\n${traumaSummary.trimEnd()}\r\n\r\n`;

      // Alcohol/Drug Use History
      if (
        data.SubstanceUseHistory["@value"] &&
        data.SubstanceUseHistory["@value"] !== ""
      ) {
        const substances = JSON.parse(
          data.SubstanceUseHistory["@value"]
        ).filter((item) => !item.IsEditable);

        if (substances.length > 0)
          summary += `Alcohol/Drug Use History:\r\n${substances
            .map(
              (item) =>
                `${item.FamilyMember}: ${item.SubstancesUsed}, Concerns: ${item.Concerns}, Impact: ${item.Impact}`
            )
            .join(". ")}\r\n\r\n`;
      }

      // Lethality
      let lethalitySummary = "";
      let selfHarmSummary = "";
      const selfHarmDetails = JSON.parse(data.SelfHarmDetails["@value"]).filter(
        (item) => !item.IsEditable
      );

      if (selfHarmDetails.length > 0)
        selfHarmSummary += `${selfHarmDetails
          .map(
            (item, index) =>
              `${
                item.FamilyMember && item.FamilyMember !== ""
                  ? `${item.FamilyMember}: `
                  : ""
              }${getSelfHarmRisk(index)}, Comments: ${item.Comments}`
          )
          .join(". ")}; `;

      if (
        data.NotAllSelfHarmScreenedDetails["@value"] &&
        data.NotAllSelfHarmScreenedDetails["@value"] !== ""
      )
        selfHarmSummary += `Reason why not all family members were screened: ${data.NotAllSelfHarmScreenedDetails["@value"]}; `;

      if (
        data.SelfHarmComments["@value"] &&
        data.SelfHarmComments["@value"] !== ""
      )
        selfHarmSummary += `Comments/Action Taken: ${data.SelfHarmComments["@value"]}`;

      if (selfHarmSummary !== "")
        lethalitySummary += `Suicide/Harm to Self:\r\n${selfHarmSummary}\r\n\r\n`;

      let harmOthersSummary = "";
      const harmingOthersDetails = JSON.parse(
        data.HarmingOthersDetails["@value"]
      ).filter((item) => !item.IsEditable);

      if (harmingOthersDetails.length > 0)
        harmOthersSummary += `${harmingOthersDetails
          .map(
            (item, index) =>
              `${
                item.FamilyMember && item.FamilyMember !== ""
                  ? `${item.FamilyMember}: `
                  : ""
              }${getHarmOthersRisk(index)}, Comments: ${item.Comments}`
          )
          .join(". ")}; `;

      if (
        data.NotAllHarmOthersScreenedDetails["@value"] &&
        data.NotAllHarmOthersScreenedDetails["@value"] !== ""
      )
        harmOthersSummary += `Reason why not all family members were screened: ${data.NotAllHarmOthersScreenedDetails["@value"]}; `;

      if (
        data.HarmingOthersComments["@value"] &&
        data.HarmingOthersComments["@value"] !== ""
      )
        harmOthersSummary += `Comments/Action Taken: ${data.HarmingOthersComments["@value"]}`;

      if (harmOthersSummary !== "")
        lethalitySummary += `Harm to Others/Endangerment:\r\n${harmOthersSummary};`;

      if (lethalitySummary !== "")
        summary += `Lethality:\r\n${lethalitySummary}\r\n\r\n`;

      // Clinical Narrative Summary Comments
      if (
        data.ClinicalSummaryComments["@value"] &&
        data.ClinicalSummaryComments["@value"] !== ""
      )
        summary += `Additional Comments:\r\n${data.ClinicalSummaryComments["@value"]}`;

      summary = summary.trimEnd();

      const obj = { ...data };
      obj.ClinicalSummary["@value"] = summary;
      setData(obj);
    }
  };

  const customValidate = (errs) => {
    if (
      (!data.ISFamily["@value"] || data.ISFamily["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISDCS["@value"] || data.ISDCS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    // const treatmentRecommendations = JSON.parse(
    //   data.TreatmentRecommendations["@value"]
    // );

    // if (treatmentRecommendations.length === 0) {
    //   if (errs)
    //     errs.TreatmentRecommendations =
    //       "Treatment recommendations are required.";
    //   else
    //     errs = {
    //       TreatmentRecommendations: "Treatment recommendations are required.",
    //     };
    // }

    // if (data.NoSelfHarm["@value"] !== "true") {
    //   const selfHarmDetails = JSON.parse(data.SelfHarmDetails["@value"]);

    //   if (selfHarmDetails.length === 0) {
    //     if (errs)
    //       errs.SelfHarmDetails =
    //         "If there is a history or current risk of self-harm, details are required.";
    //     else
    //       errs = {
    //         SelfHarmDetails:
    //           "If there is a history or current risk of self-harm, details are required.",
    //       };
    //   }
    // }

    // if (data.NoHarmingOthers["@value"] !== "true") {
    //   const harmingOthersDetails = JSON.parse(
    //     data.HarmingOthersDetails["@value"]
    //   );

    //   if (harmingOthersDetails.length === 0) {
    //     if (errs)
    //       errs.HarmingOthersDetails =
    //         "If there is a history or current risk of harming others, details are required.";
    //     else
    //       errs = {
    //         HarmingOthersDetails:
    //           "If there is a history or current risk of harming others, details are required.",
    //       };
    //   }
    // }

    // const educationDetails = JSON.parse(data.EducationDetails["@value"]);

    // if (educationDetails.length === 0) {
    //   if (errs) errs.EducationDetails = "Education details are required.";
    //   else
    //     errs = {
    //       EducationDetails: "Education details are required.",
    //     };
    // }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "ReferralInformation", name: "Referral Information" },
    { _id: "FamilyHistorySection", name: "Family Environment and History" },
    {
      _id: "Demographics",
      name: "Family Culture/Ethnicity/Religion/Spirituality",
    },
    { _id: "FamilyWellbeing", name: "Family Wellbeing" },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "Lethality", name: "Lethality" },
    { _id: "Education", name: "Education/Employment" },
    {
      _id: "SubstanceUseHistory",
      name: "Alcohol/Drug Use History",
    },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    {
      _id: "Activities",
      name: "Activities of Daily Living",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "AssessmentSummary", name: "Assessment Summary" },
    { _id: "Diagnosis", name: "Diagnosis/Evaluation" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={(e) => {
            preSignForm();
            toggleSignatureModal(e);
          }}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="SexAssignedAtBirth"
                    label="Sex Assigned at Birth:"
                    error={errors.SexAssignedAtBirth}
                    value={data.SexAssignedAtBirth["@value"]}
                    options={sexAtBirthOptions}
                    onChange={handleChange}
                    identical={compare("SexAssignedAtBirth")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.SexAssignedAtBirth !== undefined &&
                    data.SexAssignedAtBirth["@value"] === "Sex not listed" && (
                      <FormInput
                        name="SexAssignedAtBirthDetails"
                        label="Please specify:"
                        error={errors.SexAssignedAtBirthDetails}
                        value={data.SexAssignedAtBirthDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("SexAssignedAtBirthDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
                <div className="col">
                  <FormSelect
                    name="Pronouns"
                    label="Pronouns:"
                    error={errors.Pronouns}
                    value={data.Pronouns["@value"]}
                    options={pronounOptions}
                    onChange={handleChange}
                    identical={compare("Pronouns")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.Pronouns !== undefined &&
                    data.Pronouns["@value"] === "Prefer to self-describe" && (
                      <FormInput
                        name="PronounsDescription"
                        label="Please describe:"
                        error={errors.PronounsDescription}
                        value={data.PronounsDescription["@value"]}
                        onChange={handleChange}
                        identical={compare("PronounsDescription")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
                <div className="col">
                  <FormSelect
                    name="GenderIdentity"
                    label="Gender Identity:"
                    error={errors.GenderIdentity}
                    value={data.GenderIdentity["@value"]}
                    options={genderIdentityOptions}
                    onChange={handleChange}
                    identical={compare("GenderIdentity")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.GenderIdentity !== undefined &&
                    data.GenderIdentity["@value"] === "Identity not listed" && (
                      <FormInput
                        name="GenderIdentityDescription"
                        label="Please specify:"
                        error={errors.GenderIdentityDescription}
                        value={data.GenderIdentityDescription["@value"]}
                        onChange={handleChange}
                        identical={compare("GenderIdentityDescription")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="DateCompleted"
                    label="Date of Assessment:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormRadioButton
                    name="PSDType"
                    label="Type of Assessment:"
                    error={errors.PSDType}
                    value={data.PSDType["@value"]}
                    options={psdTypeOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.PSDType["@value"] === "Review/Update" && (
                    <FormInput
                      name="ReviewReason"
                      label="Reason:"
                      error={errors.ReviewReason}
                      value={getFieldValue(data.ReviewReason)}
                      onChange={handleChange}
                      identical={compare("ReviewReason")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="ReferralMembers"
                    label="Family Members on Referral:"
                    error={errors.ReferralMembers}
                    value={getFieldValue(data.ReferralMembers)}
                    onChange={handleChange}
                    identical={compare("ReferralMembers")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="MaGIKCaseID"
                    label="MaGIK Case ID:"
                    error={errors.MaGIKCaseID}
                    value={getFieldValue(data.MaGIKCaseID)}
                    onChange={handleChange}
                    identical={compare("MaGIKCaseID")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="Caseworker"
                    label="Caseworker:"
                    error={errors.Caseworker}
                    value={getFieldValue(data.Caseworker)}
                    onChange={handleChange}
                    identical={compare("Caseworker")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormSelect
                    name="Division"
                    label="SAFY Division:"
                    error={errors.Division}
                    value={getFieldValue(data.Division)}
                    options={divisionOptions}
                    onChange={handleChange}
                    identical={compare("Division")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormSelect
                    name="County"
                    label="County:"
                    error={errors.County}
                    value={getFieldValue(data.County)}
                    options={countyOptions}
                    onChange={handleChange}
                    identical={compare("County")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.County) === "Other" && (
                    <FormInput
                      name="CountyDetails"
                      error={errors.CountyDetails}
                      value={getFieldValue(data.CountyDetails)}
                      onChange={handleChange}
                      identical={compare("CountyDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISFamily"
                      label="Family Members"
                      checked={data.ISFamily["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISServiceProvider"
                      label="Other Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISDCS"
                      label="DCS"
                      checked={data.ISDCS["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="ISOther"
                      label="Other/Comments"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ISOther["@value"] === "true" && (
                      <FormInput
                        name="ISOtherDetails"
                        error={errors.ISOtherDetails}
                        value={data.ISOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("ISOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col"></div>
                </div>
              </div>
              {errors.InformationSources !== undefined && (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              )}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">
                    Clinical Observations/Family Clinical Summary:
                  </div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="ReferralInformation"
                expandStatus={expandStatus}
                className="mt-2"
                title="Referral Information"
                tooltip="Precipitating factors that led to the need for this assessment. Please include a statement in the family's own words as well as sources for the assessment/family's response to referral"
              >
                <FormInput
                  name="ReferralReason"
                  label="Reason for Referral:"
                  error={errors.ReferralReason}
                  value={data.ReferralReason["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ReferralReason")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormTextArea
                  name="PresentingProblem"
                  label="Family's Description of the Problem(s):"
                  error={errors.PresentingProblem}
                  value={data.PresentingProblem["@value"]}
                  onChange={handleChange}
                  identical={compare("PresentingProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormTextArea
                  name="ProtectiveFactors"
                  label="Protective Factors, Family Strengths, and Interests:"
                  error={errors.ProtectiveFactors}
                  value={data.ProtectiveFactors["@value"]}
                  onChange={handleChange}
                  identical={compare("ProtectiveFactors")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment and History"
              >
                <div title="Individuals currently in the home, ages, DOB, family role">
                  Current Household: <i className="fas fa-info-circle"></i>
                </div>
                <FormTextArea
                  name="CurrentHousehold"
                  error={errors.CurrentHousehold}
                  value={data.CurrentHousehold["@value"]}
                  onChange={handleChange}
                  identical={compare("CurrentHousehold")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div title="Individuals who have a close relationship to client, not living in the home. Include their relationship to the family, their role in family's life, and how they serve as a support to the family.">
                  Other Family Supports: <i className="fas fa-info-circle"></i>
                </div>
                <FormTextArea
                  name="OtherFamilySupports"
                  error={errors.OtherFamilySupports}
                  value={data.OtherFamilySupports["@value"]}
                  onChange={handleChange}
                  identical={compare("OtherFamilySupports")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div className="row pt-2">
                  <div className="col">
                    <FormRadioButton
                      name="CaregiverCooperation"
                      label="Cooperation between caregivers on discipline and care:"
                      error={errors.CaregiverCooperation}
                      value={data.CaregiverCooperation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="SupervisionDifficulties"
                      label="Difficulties with the supervision/monitoring of child:"
                      error={errors.SupervisionDifficulties}
                      value={data.SupervisionDifficulties["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row pt-2">
                  <div className="col">
                    {data.CaregiverCooperation["@value"] === "No" && (
                      <FormInput
                        name="CaregiverCooperationDetails"
                        label="Explain:"
                        error={errors.CaregiverCooperationDetails}
                        value={data.CaregiverCooperationDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("CaregiverCooperationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col">
                    {data.SupervisionDifficulties["@value"] === "Yes" && (
                      <FormInput
                        name="SupervisionDifficultiesDetails"
                        label="Describe:"
                        error={errors.SupervisionDifficultiesDetails}
                        value={data.SupervisionDifficultiesDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("SupervisionDifficultiesDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <FormTextArea
                  name="DisciplinePractices"
                  label="Discipline practices:"
                  error={errors.DisciplinePractices}
                  value={data.DisciplinePractices["@value"]}
                  onChange={handleChange}
                  identical={compare("DisciplinePractices")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormTextArea
                  name="Interaction"
                  label="Interaction between caregivers and child(ren):"
                  error={errors.Interaction}
                  value={data.Interaction["@value"]}
                  onChange={handleChange}
                  identical={compare("Interaction")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div title="Domestic Violence, Criminal Activity, Substance Use/Abuse, Abuse/Neglect, Psychiatric/Neurological/Medical Conditions, etc.">
                  Extended Family History:{" "}
                  <i className="fas fa-info-circle"></i>
                </div>
                <FormTextArea
                  name="ExtendedFamilyHistory"
                  error={errors.ExtendedFamilyHistory}
                  value={data.ExtendedFamilyHistory["@value"]}
                  onChange={handleChange}
                  identical={compare("ExtendedFamilyHistory")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div title="Include information on social relationships/friendships, involvement in the community, bullying concerns, peer associations for children, play skills, socioemotional functioning, communication skills and problem-solving.">
                  Psychosocial Functioning:{" "}
                  <i className="fas fa-info-circle"></i>
                </div>
                <FormTextArea
                  name="PsychosocialFunctioning"
                  error={errors.PsychosocialFunctioning}
                  value={data.PsychosocialFunctioning["@value"]}
                  onChange={handleChange}
                  identical={compare("PsychosocialFunctioning")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="Demographics"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Culture/Ethnicity/Religion/Spirituality"
              >
                <div className="container">
                  <div className="row">
                    <div
                      className="col"
                      title="Culture broadly refers to all the ways an individual understands their identity in terms of race, ethnicity, gender, sexual orientation, social class, religion/spirituality and language"
                    >
                      Identified cultural group:{" "}
                      <i className="fas fa-info-circle"></i>
                    </div>
                    <div className="col">
                      <FormInput
                        name="CulturalGroup"
                        error={errors.CulturalGroup}
                        value={data.CulturalGroup["@value"]}
                        onChange={handleChange}
                        identical={compare("CulturalGroup")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Special cultural traditions/celebrations/rituals:
                    </div>
                    <div className="col">
                      <FormInput
                        name="TraditionsComments"
                        error={errors.TraditionsComments}
                        value={data.TraditionsComments["@value"]}
                        onChange={handleChange}
                        identical={compare("TraditionsComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Languages spoken:</div>
                    <div className="col">
                      <FormInput
                        name="LanguagesSpoken"
                        error={errors.LanguagesSpoken}
                        value={data.LanguagesSpoken["@value"]}
                        onChange={handleChange}
                        identical={compare("LanguagesSpoken")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Identified religion/belief system:
                    </div>
                    <div className="col">
                      <FormInput
                        name="Religion"
                        error={errors.Religion}
                        value={data.Religion["@value"]}
                        onChange={handleChange}
                        identical={compare("Religion")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Role of faith in client's life:</div>
                    <div className="col">
                      <FormInput
                        name="ReligionRole"
                        error={errors.ReligionRole}
                        value={data.ReligionRole["@value"]}
                        onChange={handleChange}
                        identical={compare("ReligionRole")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="FamilyWellbeing"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Wellbeing"
                tooltip="Please make sure you list which family member(s) has(have) the identified medical issues."
              >
                <FormInput
                  name="PCP"
                  label="Primary Care Physician:"
                  error={errors.PCP}
                  value={data.PCP["@value"]}
                  onChange={handleChange}
                  identical={compare("PCP")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="LastMedicalExamDates"
                  label="Date(s) of last medical exam for child(ren):"
                  error={errors.LastMedicalExamDates}
                  value={data.LastMedicalExamDates["@value"]}
                  onChange={handleChange}
                  identical={compare("LastMedicalExamDates")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div className="container">
                  <div className="row">
                    <div className="col">Issue(s)</div>
                    <div className="col">Family Member(s) and Description</div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Allergies"
                        label="Allergies"
                        divClassName="ms-3"
                        checked={data.Allergies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Allergies["@value"] === "true" && (
                        <FormInput
                          name="AllergiesDetails"
                          error={errors.AllergiesDetails}
                          value={data.AllergiesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("AllergiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="MedicalConditions"
                        label="Medical conditions/concerns"
                        divClassName="ms-3"
                        checked={data.MedicalConditions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MedicalConditions["@value"] === "true" && (
                        <FormInput
                          name="MedicalConditionsDetails"
                          error={errors.MedicalConditionsDetails}
                          value={data.MedicalConditionsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MedicalConditionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="NutritionalConcerns"
                        label="Nutritional concerns"
                        divClassName="ms-3"
                        checked={data.NutritionalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.NutritionalConcerns["@value"] === "true" && (
                        <FormInput
                          name="NutritionalConcernsDetails"
                          error={errors.NutritionalConcernsDetails}
                          value={data.NutritionalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("NutritionalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="DentalConcerns"
                        label="Dental concerns"
                        divClassName="ms-3"
                        checked={data.DentalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DentalConcerns["@value"] === "true" && (
                        <FormInput
                          name="DentalConcernsDetails"
                          error={errors.DentalConcernsDetails}
                          value={data.DentalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("DentalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="SleepConcerns"
                        label="Sleep concerns"
                        divClassName="ms-3"
                        checked={data.SleepConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.SleepConcerns["@value"] === "true" && (
                        <FormInput
                          name="SleepConcernsDetails"
                          error={errors.SleepConcernsDetails}
                          value={data.SleepConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SleepConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Hospitalizations"
                        label="Hospitalizations/Surgery"
                        divClassName="ms-3"
                        checked={data.Hospitalizations["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Hospitalizations["@value"] === "true" && (
                        <FormInput
                          name="HospitalizationsDetails"
                          error={errors.HospitalizationsDetails}
                          value={data.HospitalizationsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HospitalizationsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="HeadInjury"
                        label="Head Injury"
                        divClassName="ms-3"
                        checked={data.HeadInjury["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.HeadInjury["@value"] === "true" && (
                        <FormInput
                          name="HeadInjuryDetails"
                          error={errors.HeadInjuryDetails}
                          value={data.HeadInjuryDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HeadInjuryDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <FormRadioButton
                  name="Undiagnosed"
                  label="Symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                  error={errors.Undiagnosed}
                  value={data.Undiagnosed["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  className="mt-2"
                />
                {data.Undiagnosed["@value"] === "Yes" && (
                  <FormTextArea
                    name="UndiagnosedComments"
                    label="Comments:"
                    error={errors.UndiagnosedComments}
                    value={data.UndiagnosedComments["@value"]}
                    onChange={handleChange}
                    identical={compare("UndiagnosedComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <div title="Prenatal care, exposure to tobacco/alcohol/drugs/violence, birth complications, etc.">
                  Pregnancy History: <i className="fas fa-info-circle"></i>
                </div>
                <FormTextArea
                  name="PregnancyHistory"
                  error={errors.PregnancyHistory}
                  value={data.PregnancyHistory["@value"]}
                  onChange={handleChange}
                  identical={compare("PregnancyHistory")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Developmental History of Children:</h5>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      Developmental milestones met on time:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="MilestonesMet"
                        error={errors.MilestonesMet}
                        value={data.MilestonesMet["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.MilestonesMet["@value"] === "No" ? (
                        <FormInput
                          name="MilestonesMetDetails"
                          label="Describe:"
                          error={errors.MilestonesMetDetails}
                          value={data.MilestonesMetDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MilestonesMetDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Developmental delays or special needs:
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="SpecialNeeds"
                        error={errors.SpecialNeeds}
                        value={data.SpecialNeeds["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecialNeeds["@value"] === "Yes" && (
                        <FormTextArea
                          name="SpecialNeedsDetails"
                          label="Describe:"
                          error={errors.SpecialNeedsDetails}
                          value={data.SpecialNeedsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SpecialNeedsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Specialized Services (Speech/Occupational/Physical
                      Therapy/Assistive Devices):
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="SpecializedServices"
                        error={errors.SpecializedServices}
                        value={data.SpecializedServices["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecializedServices["@value"] === "Yes" && (
                        <FormInput
                          name="SpecializedServicesDetails"
                          label="Describe:"
                          error={errors.SpecializedServicesDetails}
                          value={data.SpecializedServicesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SpecializedServicesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Gender identity &amp; sexual orientation:
                    </div>
                    <div className="col">
                      <FormInput
                        name="GenderOrientation"
                        error={errors.GenderOrientation}
                        value={data.GenderOrientation["@value"]}
                        onChange={handleChange}
                        identical={compare("GenderOrientation")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <h5>Medications</h5>
                <FormCheckbox
                  name="NoMedications"
                  label="No family members currently or previously on medications"
                  divClassName="ms-3"
                  checked={data.NoMedications["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoMedications["@value"] !== "true" && (
                  <>
                    <FormInput
                      name="CurrentMedications"
                      label="Current Medications:"
                      error={errors.CurrentMedications}
                      value={data.CurrentMedications["@value"]}
                      onChange={handleChange}
                      identical={compare("CurrentMedications")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      tooltip="Type of medication(s), dosage, purpose, etc."
                    />
                    <FormInput
                      name="PsychotropicMedications"
                      label="Past Psychotropic Medications:"
                      error={errors.PsychotropicMedications}
                      value={data.PsychotropicMedications["@value"]}
                      onChange={handleChange}
                      identical={compare("PsychotropicMedications")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      tooltip="(who, what and reason)"
                    />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="MentalHealthTreatmentHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Health Treatment History"
              >
                <FormCheckbox
                  name="NoMentalHealthTreatment"
                  label="No history of Mental Health Treatment:"
                  divClassName="ms-3"
                  error={errors.NoMentalHealthTreatment}
                  value={data.NoMentalHealthTreatment["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.NoMentalHealthTreatment["@value"] !== "true" && (
                  <FormTextArea
                    name="MentalHealthTreatment"
                    label="Mental Health Treatment:"
                    error={errors.MentalHealthTreatment}
                    value={data.MentalHealthTreatment["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("MentalHealthTreatment")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    tooltip="Reason for treatment, dates/provider, satisfaction, outcomes, etc."
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <div title="Includes current and past self-injurious behavior, suicidal gestures/thoughts/behaviors, and previous attempts.">
                  <h5>
                    Suicide/Harm to Self: <i className="fas fa-info-circle"></i>
                  </h5>
                </div>
                <FormCheckbox
                  name="NoSelfHarm"
                  label="No history or current risk of suicide/self-harm"
                  divClassName="ms-3"
                  checked={data.NoSelfHarm["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoSelfHarm["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="SelfHarmDetails"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">
                            Family Member
                          </div>
                          <div className="col border-end py-2">
                            History or Current Risk
                          </div>
                          <div className="col py-2">Comments</div>
                        </div>
                        {data.SelfHarmDetails["@value"]
                          ? JSON.parse(data.SelfHarmDetails["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveFieldTableRow(
                                              data,
                                              setData,
                                              index,
                                              "SelfHarmDetails",
                                              buildClinicalNarrativeSummary
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteFieldTableRow(
                                              data,
                                              setData,
                                              index,
                                              "SelfHarmDetails",
                                              buildClinicalNarrativeSummary
                                            );
                                          }}
                                        ></i>
                                        <FormInput
                                          name="FamilyMember"
                                          value={item.FamilyMember}
                                          onChange={(e) => {
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditFieldTableRow(
                                              data,
                                              setData,
                                              index,
                                              "SelfHarmDetails",
                                              buildClinicalNarrativeSummary
                                            );
                                          }}
                                        ></i>
                                        {item.FamilyMember}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <FormCheckbox
                                          name={`SelfAbusive-${index}`}
                                          label="Self-injurious or self-abusive"
                                          divClassName="ms-3"
                                          checked={item.SelfAbusive === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <FormCheckbox
                                          name={`SuicidalThreats-${index}`}
                                          label="Suicidal threats/gestures"
                                          divClassName="ms-3"
                                          checked={
                                            item.SuicidalThreats === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <FormCheckbox
                                          name={`SuicideAttempt-${index}`}
                                          label="Suicide attempt"
                                          divClassName="ms-3"
                                          checked={
                                            item.SuicideAttempt === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      getSelfHarmRisk(index)
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleFieldTableChange(
                                            data,
                                            e,
                                            index,
                                            "SelfHarmDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Comments
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddFieldTableRow(
                                data,
                                setData,
                                "SelfHarmDetails",
                                {
                                  IsEditable: true,
                                }
                              );
                            }}
                          >
                            Add Family Member
                          </button>
                        </>
                      ) : null}
                      {errors.SelfHarmDetails !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SelfHarmDetails}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                  </>
                )}
                <FormRadioButton
                  name="AllSelfHarmScreened"
                  label="All family members screened for suicidality during this assessment:"
                  error={errors.AllSelfHarmScreened}
                  value={data.AllSelfHarmScreened["@value"]}
                  options={yesNoOptions}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.AllSelfHarmScreened["@value"] === "No" && (
                  <FormInput
                    name="NotAllSelfHarmScreenedDetails"
                    label="Please explain:"
                    error={errors.NotAllSelfHarmScreenedDetails}
                    value={data.NotAllSelfHarmScreenedDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("NotAllSelfHarmScreenedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    title="Reason for treatment, dates/provider, satisfaction, outcomes, etc."
                  />
                )}
                {data.NoSelfHarm["@value"] !== "true" && (
                  <>
                    <FormTextArea
                      name="SelfHarmComments"
                      label="Comments/Action Taken:"
                      error={errors.SelfHarmComments}
                      value={data.SelfHarmComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("SelfHarmComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SelfHarmDetails["@value"] &&
                      JSON.parse(data.SelfHarmDetails["@value"]).length > 0 && (
                        <i>
                          Based on how you completed the table, please complete
                          a{" "}
                          <Link
                            to={`/client/${clientid}?tab=forms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Safety Plan
                          </Link>{" "}
                          and{" "}
                          <Link
                            to={`/client/${clientid}?tab=cpass`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            C-SSRS Assessment
                          </Link>
                          .
                        </i>
                      )}
                    <br />
                  </>
                )}
                <div title="Includes current and past threats/thoughts/acts to intentionally harm/endanger others or animals.">
                  <h5>
                    Harm to Others/Endangerment:{" "}
                    <i className="fas fa-info-circle"></i>
                  </h5>
                </div>
                <FormCheckbox
                  name="NoHarmingOthers"
                  label="No history or current risk of harm/endangerment to others"
                  divClassName="ms-3"
                  checked={data.NoHarmingOthers["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoHarmingOthers["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="HarmingOthersDetails"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">
                            Family Member
                          </div>
                          <div className="col border-end py-2">
                            History or Current Risk
                          </div>
                          <div className="col py-2">Comments</div>
                        </div>
                        {data.HarmingOthersDetails["@value"]
                          ? JSON.parse(data.HarmingOthersDetails["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveFieldTableRow(
                                              data,
                                              setData,
                                              index,
                                              "HarmingOthersDetails",
                                              buildClinicalNarrativeSummary
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteFieldTableRow(
                                              data,
                                              setData,
                                              index,
                                              "HarmingOthersDetails",
                                              buildClinicalNarrativeSummary
                                            );
                                          }}
                                        ></i>
                                        <FormInput
                                          name="FamilyMember"
                                          value={item.FamilyMember}
                                          onChange={(e) => {
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "HarmingOthersDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditFieldTableRow(
                                              data,
                                              setData,
                                              index,
                                              "HarmingOthersDetails",
                                              buildClinicalNarrativeSummary
                                            );
                                          }}
                                        ></i>
                                        {item.FamilyMember}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <FormCheckbox
                                          name={`Thoughts-${index}`}
                                          label="Thoughts of intentionally harming/endangering others"
                                          divClassName="ms-3"
                                          checked={item.Thoughts === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <FormCheckbox
                                          name={`Threats-${index}`}
                                          label="Threats to intentionally harm/endanger others"
                                          divClassName="ms-3"
                                          checked={item.Threats === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <FormCheckbox
                                          name={`HarmOthers-${index}`}
                                          label="Acts of intentionally harming/endangering others"
                                          divClassName="ms-3"
                                          checked={item.HarmOthers === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <FormCheckbox
                                          name={`HarmAnimals-${index}`}
                                          label="Acts of intentionally harming/endangering animals"
                                          divClassName="ms-3"
                                          checked={item.HarmAnimals === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      getHarmOthersRisk(index)
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <FormInput
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleFieldTableChange(
                                            data,
                                            e,
                                            index,
                                            "HarmingOthersDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Comments
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddFieldTableRow(
                                data,
                                setData,
                                "HarmingOthersDetails",
                                {
                                  IsEditable: true,
                                }
                              );
                            }}
                          >
                            Add Risk
                          </button>
                        </>
                      ) : null}
                      {errors.HarmingOthersDetails !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.HarmingOthersDetails}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                  </>
                )}
                <FormRadioButton
                  name="AllHarmOthersScreened"
                  label="All family members screened for risk of harm/endangerment to others during this assessment:"
                  error={errors.AllHarmOthersScreened}
                  value={data.AllHarmOthersScreened["@value"]}
                  options={yesNoOptions}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.AllHarmOthersScreened["@value"] === "No" && (
                  <FormInput
                    name="NotAllHarmOthersScreenedDetails"
                    label="Please explain"
                    error={errors.NotAllHarmOthersScreenedDetails}
                    value={data.NotAllHarmOthersScreenedDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("NotAllHarmOthersScreenedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    title="Reason for treatment, dates/provider, satisfaction, outcomes, etc."
                  />
                )}
                {data.NoHarmingOthers["@value"] !== "true" && (
                  <>
                    <FormTextArea
                      name="HarmingOthersComments"
                      label="Comments/Action Taken:"
                      error={errors.HarmingOthersComments}
                      value={data.HarmingOthersComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("HarmingOthersComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.HarmingOthersDetails["@value"] &&
                      JSON.parse(data.HarmingOthersDetails["@value"]).length >
                        0 && (
                        <i>
                          Based on how you completed the table, please complete
                          a{" "}
                          <Link
                            to={`/client/${clientid}?tab=forms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Safety Plan
                          </Link>{" "}
                          and{" "}
                          <Link
                            to={`/client/${clientid}?tab=cpass`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            C-SSRS Assessment
                          </Link>
                          .
                        </i>
                      )}
                    <br />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <FieldTable
                  name="EducationDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Family Member</div>
                      <div className="col border-end py-2">
                        Current Grade or Highest Grade Completed
                      </div>
                      <div className="col py-2">
                        Discipline Concerns / Achievements / Supports, etc.
                      </div>
                    </div>
                    {data.EducationDetails["@value"]
                      ? JSON.parse(data.EducationDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "EducationDetails",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "EducationDetails",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    <FormInput
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "EducationDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "EducationDetails",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Grade"
                                    value={item.Grade}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "EducationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Grade
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormInput
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "EducationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )
                      : null}
                  </div>
                  {!formDisabled ? (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddFieldTableRow(
                            data,
                            setData,
                            "EducationDetails",
                            {
                              IsEditable: true,
                            }
                          );
                        }}
                      >
                        Add Education Information
                      </button>
                    </>
                  ) : null}
                  {errors.EducationDetails !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.EducationDetails}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
                <h5>Employment</h5>
                <FormCheckbox
                  name="NoneEmployed"
                  label="No family members currently employed"
                  divClassName="ms-3"
                  checked={data.NoneEmployed["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoneEmployed["@value"] !== "true" && (
                  <FieldTable
                    name="EmploymentDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">
                          Place of Employment
                        </div>
                        <div className="col py-2">
                          Issues / Achievements / Barriers to Employment
                          Stability
                        </div>
                      </div>
                      {data.EmploymentDetails["@value"]
                        ? JSON.parse(data.EmploymentDetails["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveFieldTableRow(
                                            data,
                                            setData,
                                            index,
                                            "EmploymentDetails",
                                            buildClinicalNarrativeSummary
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteFieldTableRow(
                                            data,
                                            setData,
                                            index,
                                            "EmploymentDetails",
                                            buildClinicalNarrativeSummary
                                          );
                                        }}
                                      ></i>
                                      <FormInput
                                        name="FamilyMember"
                                        value={item.FamilyMember}
                                        onChange={(e) => {
                                          handleFieldTableChange(
                                            data,
                                            e,
                                            index,
                                            "EmploymentDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditFieldTableRow(
                                            data,
                                            setData,
                                            index,
                                            "EmploymentDetails",
                                            buildClinicalNarrativeSummary
                                          );
                                        }}
                                      ></i>
                                      {item.FamilyMember}
                                    </>
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Workplace"
                                      value={item.Workplace}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "EmploymentDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Workplace
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Comments"
                                      value={item.Comments}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "EmploymentDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Comments
                                  )}
                                </div>
                              </div>
                            )
                          )
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddFieldTableRow(
                              data,
                              setData,
                              "EmploymentDetails",
                              {
                                IsEditable: true,
                              }
                            );
                          }}
                        >
                          Add Employment Information
                        </button>
                      </>
                    ) : null}
                    {errors.EmploymentDetails !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.EmploymentDetails}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                )}
                <FormRadioButton
                  name="MilitaryHistory"
                  label="Family members with Military History"
                  error={errors.MilitaryHistory}
                  value={data.MilitaryHistory["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.MilitaryHistory["@value"] === "Yes" && (
                  <FormInput
                    name="MilitaryHistoryDetails"
                    label="Who, dates of service, branch and discharge reason"
                    error={errors.MilitaryHistoryDetails}
                    value={data.MilitaryHistoryDetails["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="SubstanceUseHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Alcohol/Drug Use History"
              >
                <FormCheckbox
                  name="NoSubstanceUse"
                  label="No family members with current or past substance use"
                  divClassName="ms-3"
                  error={errors.NoSubstanceUse}
                  checked={data.NoSubstanceUse["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.NoSubstanceUse["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="SubstanceUseHistory"
                      commentsHandler={handleComments}
                    >
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col-2 border-end py-2">
                            Family Member
                          </div>
                          <div className="col-3 border-end py-2">
                            <label title="past, most recent, and current, amount, frequency, etc.">
                              Substances Used{" "}
                              <i className="fas fa-info-circle"></i>
                            </label>
                          </div>
                          <div className="col-4 border-end py-2">
                            <label title="i.e., withdrawal, detox needed, hospitalizations, overdose, legal charges, past treatment, etc.">
                              Issues / Concerns{" "}
                              <i className="fas fa-info-circle"></i>
                            </label>
                          </div>
                          <div className="col-3 py-2">
                            Impact on Functioning / Relationships
                          </div>
                        </div>
                        {data.SubstanceUseHistory["@value"] &&
                          JSON.parse(data.SubstanceUseHistory["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col-2 border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveFieldTableRow(
                                            data,
                                            setData,
                                            index,
                                            "SubstanceUseHistory",
                                            buildClinicalNarrativeSummary
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteFieldTableRow(
                                            data,
                                            setData,
                                            index,
                                            "SubstanceUseHistory",
                                            buildClinicalNarrativeSummary
                                          );
                                        }}
                                      ></i>
                                      <FormInput
                                        name="FamilyMember"
                                        value={item.FamilyMember}
                                        onChange={(e) => {
                                          handleFieldTableChange(
                                            data,
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditFieldTableRow(
                                            data,
                                            setData,
                                            index,
                                            "SubstanceUseHistory",
                                            buildClinicalNarrativeSummary
                                          );
                                        }}
                                      ></i>
                                      {item.FamilyMember}
                                    </>
                                  )}
                                </div>
                                <div className="col-3 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormTextArea
                                      name="SubstancesUsed"
                                      value={item.SubstancesUsed}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.SubstancesUsed
                                  )}
                                </div>
                                <div className="col-4 border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormTextArea
                                      name="Concerns"
                                      value={item.Concerns}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Concerns
                                  )}
                                </div>
                                <div className="col-3 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <FormInput
                                      name="Impact"
                                      value={item.Impact}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Impact
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {!formDisabled && (
                        <>
                          <button
                            className="btn btn-primary my-2"
                            onClick={() => {
                              handleAddFieldTableRow(
                                data,
                                setData,
                                "SubstanceUseHistory",
                                {
                                  IsEditable: true,
                                }
                              );
                            }}
                          >
                            Add Family Member
                          </button>
                        </>
                      )}
                      {errors.SubstanceUseHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SubstanceUseHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                <FieldTable
                  name="TraumaHistory"
                  commentsHandler={handleComments}
                >
                  <br />
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">Family Member</div>
                      <div className="col-3 border-end py-2">
                        Type of Trauma(s)
                      </div>
                      <div className="col-6 py-2">Comments</div>
                    </div>
                    {data.TraumaHistory["@value"]
                      ? JSON.parse(data.TraumaHistory["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-3 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "TraumaHistory",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "TraumaHistory",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    <FormInput
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "TraumaHistory",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col-3 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    {item.Types !== undefined &&
                                      item.Types.map((type, typesIndex) => (
                                        <div
                                          key={`Types.${typesIndex}`}
                                          className="d-flex"
                                        >
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              item.Types = item.Types.slice(
                                                0,
                                                typesIndex
                                              ).concat(
                                                item.Types.slice(typesIndex + 1)
                                              );
                                              handleFieldTableChange(
                                                data,
                                                {
                                                  currentTarget: {
                                                    name: "Types",
                                                    value: item.Types,
                                                    type: "array",
                                                  },
                                                },
                                                index,
                                                "TraumaHistory"
                                              );
                                            }}
                                          ></i>
                                          {type}
                                        </div>
                                      ))}
                                    <FormSelect
                                      name="Types"
                                      options={
                                        item.Types !== undefined
                                          ? traumaTypeOptions.filter(
                                              (option) =>
                                                item.Types.filter(
                                                  (type) => type === option._id
                                                ).length === 0
                                            )
                                          : traumaTypeOptions
                                      }
                                      onChange={(e) => {
                                        item.Types !== undefined
                                          ? item.Types.push(
                                              e.currentTarget.value
                                            )
                                          : (item.Types = [
                                              e.currentTarget.value,
                                            ]);

                                        handleFieldTableChange(
                                          data,
                                          {
                                            currentTarget: {
                                              name: "Types",
                                              value: item.Types,
                                              type: "array",
                                            },
                                          },
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : item.Types !== undefined ? (
                                  item.Types.join(", ")
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="col-6 py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormTextArea
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "TraumaHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )
                      : null}
                  </div>
                  {!formDisabled && (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddFieldTableRow(
                            data,
                            setData,
                            "TraumaHistory",
                            {
                              IsEditable: true,
                            }
                          );
                        }}
                      >
                        Add Family Member
                      </button>
                    </>
                  )}
                  {errors.TraumaHistory !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TraumaHistory}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <FormRadioButton
                  name="FamilyChanges"
                  label="Changes/transitions in family:"
                  error={errors.FamilyChanges}
                  value={data.FamilyChanges["@value"]}
                  options={yesNoOptions}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.FamilyChanges["@value"] === "Yes" && (
                  <FormInput
                    name="FamilyChangesDetails"
                    label="Describe:"
                    error={errors.FamilyChangesDetails}
                    value={data.FamilyChangesDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("FamilyChangesDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <FormRadioButton
                  name="RecentGrief"
                  label="Recent or significant deaths/grief issues:"
                  error={errors.RecentGrief}
                  value={data.RecentGrief["@value"]}
                  options={yesNoOptions}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.RecentGrief["@value"] === "Yes" && (
                  <FormInput
                    name="RecentGriefDetails"
                    label="Describe:"
                    error={errors.RecentGriefDetails}
                    value={data.RecentGriefDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("RecentGriefDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="SymptomChecklist"
                expandStatus={expandStatus}
                className="mt-2"
                title="Symptom Checklist"
              >
                <FormCheckbox
                  name="SymptomsNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={
                    data.SymptomsNA !== undefined &&
                    data.SymptomsNA["@value"] === "true"
                  }
                  comments={parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.SymptomsNA === undefined ||
                data.SymptomsNA["@value"] !== "true" ? (
                  <FieldTable name="Symptoms" commentsHandler={handleComments}>
                    <div className="container border mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">Symptom(s)</div>
                        <div className="col py-2">
                          Description{" "}
                          <i>(including Frequency, Intensity and Duration)</i>
                        </div>
                      </div>
                      {data.Symptoms["@value"] &&
                        JSON.parse(data.Symptoms["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "Symptoms",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "Symptoms",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    <FormInput
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "Symptoms"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditFieldTableRow(
                                          data,
                                          setData,
                                          index,
                                          "Symptoms",
                                          buildClinicalNarrativeSummary
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    {item.Symptoms !== undefined &&
                                      item.Symptoms.map(
                                        (symptom, symptomsIndex) => (
                                          <div
                                            key={`Symptoms.${symptomsIndex}`}
                                            className="d-flex"
                                          >
                                            <i
                                              className="far fa-trash-alt me-2 d-print-none"
                                              onClick={() => {
                                                item.Symptoms =
                                                  item.Symptoms.slice(
                                                    0,
                                                    symptomsIndex
                                                  ).concat(
                                                    item.Symptoms.slice(
                                                      symptomsIndex + 1
                                                    )
                                                  );
                                                handleFieldTableChange(
                                                  data,
                                                  {
                                                    currentTarget: {
                                                      name: "Symptoms",
                                                      value: item.Symptoms,
                                                      type: "array",
                                                    },
                                                  },
                                                  index,
                                                  "Symptoms"
                                                );
                                              }}
                                            ></i>
                                            {symptom}
                                          </div>
                                        )
                                      )}
                                    <FormSelect
                                      name="Symptoms"
                                      options={
                                        item.Symptoms !== undefined
                                          ? symptomOptions.filter(
                                              (option) =>
                                                item.Symptoms.filter(
                                                  (symptom) =>
                                                    symptom === option._id
                                                ).length === 0
                                            )
                                          : symptomOptions
                                      }
                                      onChange={(e) => {
                                        item.Symptoms !== undefined
                                          ? item.Symptoms.push(
                                              e.currentTarget.value
                                            )
                                          : (item.Symptoms = [
                                              e.currentTarget.value,
                                            ]);

                                        handleFieldTableChange(
                                          data,
                                          {
                                            currentTarget: {
                                              name: "Symptoms",
                                              value: item.Symptoms,
                                              type: "array",
                                            },
                                          },
                                          index,
                                          "Symptoms"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Symptoms !== undefined &&
                                      item.Symptoms.filter(
                                        (symptom) => symptom === "Other"
                                      ).length > 0 && (
                                        <FormInput
                                          name="OtherSymptomDetails"
                                          value={item.OtherSymptomDetails}
                                          onChange={(e) => {
                                            handleFieldTableChange(
                                              data,
                                              e,
                                              index,
                                              "Symptoms"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      )}
                                  </>
                                ) : item.Symptoms !== undefined ? (
                                  `${item.Symptoms.join(", ")}${
                                    item.OtherSymptomDetails !== undefined &&
                                    item.OtherSymptomDetails !== ""
                                      ? `, ${item.OtherSymptomDetails}`
                                      : ""
                                  }`
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <FormTextArea
                                    name="Description"
                                    value={item.Description}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "Symptoms"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Description
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddFieldTableRow(data, setData, "Symptoms", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    ) : null}
                    {errors.Symptoms !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.Symptoms}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                {data.SymptomsNA["@value"] === "true" && (
                  <>
                    <FormInput
                      name="SymptomsNAComments"
                      label="Comments:"
                      error={errors.SymptomsNAComments}
                      value={
                        data.SymptomsNAComments &&
                        data.SymptomsNAComments["@value"]
                      }
                      onChange={handleChange}
                      identical={compare("SymptomsNAComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Activities"
                expandStatus={expandStatus}
                className="mt-2"
                title="Activities of Daily Living"
              >
                <div className="container">
                  <div className="row border-bottom mb-2">
                    <div className="col-2">
                      <b>Skill</b>
                    </div>
                    <div className="col-2">
                      <b>Support Needed</b>
                    </div>
                    <div className="col">
                      <b>Family Member(s)</b>
                    </div>
                    <div className="col">
                      <b>Explanation</b>
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">
                      Personal appearance and hygiene:
                    </div>
                    <div className="col-2">
                      <FormRadioButton
                        name="Hygiene"
                        error={errors.Hygiene}
                        value={data.Hygiene["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Hygiene["@value"] === "Yes" && (
                        <FormInput
                          name="HygieneFamilyMembers"
                          error={errors.HygieneFamilyMembers}
                          value={data.HygieneFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("HygieneFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Hygiene["@value"] === "Yes" && (
                        <FormInput
                          name="HygieneDetails"
                          error={errors.HygieneDetails}
                          value={data.HygieneDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HygieneDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Money management:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="MoneyManagement"
                        error={errors.MoneyManagement}
                        value={data.MoneyManagement["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MoneyManagement["@value"] === "Yes" && (
                        <FormInput
                          name="MoneyManagementFamilyMembers"
                          error={errors.MoneyManagementFamilyMembers}
                          value={data.MoneyManagementFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("MoneyManagementFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.MoneyManagement["@value"] === "Yes" && (
                        <FormInput
                          name="MoneyManagementDetails"
                          error={errors.MoneyManagementDetails}
                          value={data.MoneyManagementDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MoneyManagementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Food management:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="FoodManagement"
                        error={errors.FoodManagement}
                        value={data.FoodManagement["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.FoodManagement["@value"] === "Yes" && (
                        <FormInput
                          name="FoodManagementFamilyMembers"
                          error={errors.FoodManagementFamilyMembers}
                          value={data.FoodManagementFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("FoodManagementFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.FoodManagement["@value"] === "Yes" && (
                        <FormInput
                          name="FoodManagementDetails"
                          error={errors.FoodManagementDetails}
                          value={data.FoodManagementDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("FoodManagementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">
                      Health awareness / Personal Health:
                    </div>
                    <div className="col-2">
                      <FormRadioButton
                        name="Health"
                        error={errors.Health}
                        value={data.Health["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Health["@value"] === "Yes" && (
                        <FormInput
                          name="HealthFamilyMembers"
                          error={errors.HealthFamilyMembers}
                          value={data.HealthFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("HealthFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Health["@value"] === "Yes" && (
                        <FormInput
                          name="HealthDetails"
                          error={errors.HealthDetails}
                          value={data.HealthDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HealthDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Housekeeping:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="Housekeeping"
                        error={errors.Housekeeping}
                        value={data.Housekeeping["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Housekeeping["@value"] === "Yes" && (
                        <FormInput
                          name="HousekeepingFamilyMembers"
                          error={errors.HousekeepingFamilyMembers}
                          value={data.HousekeepingFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("HousekeepingFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Housekeeping["@value"] === "Yes" && (
                        <FormInput
                          name="HousekeepingDetails"
                          error={errors.HousekeepingDetails}
                          value={data.HousekeepingDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HousekeepingDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Transportation:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="Transportation"
                        error={errors.Transportation}
                        value={data.Transportation["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Transportation["@value"] === "Yes" && (
                        <FormInput
                          name="TransportationFamilyMembers"
                          error={errors.TransportationFamilyMembers}
                          value={data.TransportationFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("TransportationFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Transportation["@value"] === "Yes" && (
                        <FormInput
                          name="TransportationDetails"
                          error={errors.TransportationDetails}
                          value={data.TransportationDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("TransportationDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Emergency and safety skills:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="SafetySkills"
                        error={errors.SafetySkills}
                        value={data.SafetySkills["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.SafetySkills["@value"] === "Yes" && (
                        <FormInput
                          name="SafetySkillsFamilyMembers"
                          error={errors.SafetySkillsFamilyMembers}
                          value={data.SafetySkillsFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("SafetySkillsFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.SafetySkills["@value"] === "Yes" && (
                        <FormInput
                          name="SafetySkillsDetails"
                          error={errors.SafetySkillsDetails}
                          value={data.SafetySkillsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SafetySkillsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">
                      Knowledge of community resources:
                    </div>
                    <div className="col-2">
                      <FormRadioButton
                        name="ResourceKnowledge"
                        error={errors.ResourceKnowledge}
                        value={data.ResourceKnowledge["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.ResourceKnowledge["@value"] === "Yes" && (
                        <FormInput
                          name="ResourceKnowledgeFamilyMembers"
                          error={errors.ResourceKnowledgeFamilyMembers}
                          value={data.ResourceKnowledgeFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("ResourceKnowledgeFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.ResourceKnowledge["@value"] === "Yes" && (
                        <FormInput
                          name="ResourceKnowledgeDetails"
                          error={errors.ResourceKnowledgeDetails}
                          value={data.ResourceKnowledgeDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ResourceKnowledgeDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Time management:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="TimeManagement"
                        error={errors.TimeManagement}
                        value={data.TimeManagement["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.TimeManagement["@value"] === "Yes" && (
                        <FormInput
                          name="TimeManagementFamilyMembers"
                          error={errors.TimeManagementFamilyMembers}
                          value={data.TimeManagementFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("TimeManagementFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.TimeManagement["@value"] === "Yes" && (
                        <FormInput
                          name="TimeManagementDetails"
                          error={errors.TimeManagementDetails}
                          value={data.TimeManagementDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("TimeManagementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Interactions with Others:</div>
                    <div className="col-2">
                      <FormRadioButton
                        name="InteractionWithOthers"
                        error={errors.InteractionWithOthers}
                        value={data.InteractionWithOthers["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.InteractionWithOthers["@value"] === "Yes" && (
                        <FormInput
                          name="InteractionFamilyMembers"
                          error={errors.InteractionFamilyMembers}
                          value={data.InteractionFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("InteractionFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.InteractionWithOthers["@value"] === "Yes" && (
                        <FormInput
                          name="InteractionDetails"
                          error={errors.InteractionDetails}
                          value={data.InteractionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("InteractionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal History"
                tooltip="Type of involvement, description, dates, disposition, outcomes"
              >
                <FormRadioButton
                  name="LegalInvolvement"
                  error={errors.LegalInvolvement}
                  value={data.LegalInvolvement["@value"]}
                  options={legalHistoryOptions}
                  onChange={handleChange}
                  identical={compare("LegalInvolvement")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.LegalInvolvement["@value"] === "Yes" && (
                  <FormTextArea
                    name="LegalInvolvementDetails"
                    label="Legal History:"
                    error={errors.LegalInvolvementDetails}
                    value={data.LegalInvolvementDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("LegalInvolvementDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="AssessmentSummary"
                expandStatus={expandStatus}
                className="mt-2"
                title="Assessment Summary"
              >
                <h5>PFS-2 Scores:</h5>
                <FormRadioButton
                  name="PFS2CompletionStatus"
                  error={errors.PFS2CompletionStatus}
                  value={data.PFS2CompletionStatus["@value"]}
                  options={completedOptions}
                  onChange={handleChange}
                  identical={compare("PFS2CompletionStatus")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PFS2CompletionStatus["@value"] === "Completed" && (
                  <>
                    <div className="row">
                      <div className="col">
                        <FormInput
                          name="Resilience"
                          label="Family Functioning/Resilience:"
                          error={errors.Resilience}
                          value={data.Resilience["@value"]}
                          onChange={handleChange}
                          identical={compare("Resilience")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormInput
                          name="Attachment"
                          label="Nurturing and Attachment:"
                          error={errors.Attachment}
                          value={data.Attachment["@value"]}
                          onChange={handleChange}
                          identical={compare("Attachment")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormInput
                          name="SocialSupports"
                          label="Social Supports:"
                          error={errors.SocialSupports}
                          value={data.SocialSupports["@value"]}
                          onChange={handleChange}
                          identical={compare("SocialSupports")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <FormInput
                          name="PractitionerRelationship"
                          label="Caregiver/Practitioner Relationship:"
                          error={errors.PractitionerRelationship}
                          value={data.PractitionerRelationship["@value"]}
                          onChange={handleChange}
                          identical={compare("PractitionerRelationship")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <FormInput
                          name="ConcreteSupports"
                          label="Concrete Supports:"
                          error={errors.ConcreteSupports}
                          value={data.ConcreteSupports["@value"]}
                          onChange={handleChange}
                          identical={compare("ConcreteSupports")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </>
                )}
                <h5>Other tools used:</h5>
                <FormRadioButton
                  name="OtherTools"
                  error={errors.OtherTools}
                  value={data.OtherTools["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  identical={compare("OtherTools")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.OtherTools["@value"] === "Yes" && (
                  <FormInput
                    name="OtherToolsDetails"
                    label="Describe:"
                    error={errors.OtherToolsDetails}
                    value={data.OtherToolsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("OtherToolsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                {data.PFS2CompletionStatus["@value"] === "Completed" ||
                data.OtherTools["@value"] === "Yes" ? (
                  <>
                    <h5>Brief Interpretation of Scores:</h5>
                    <FormTextArea
                      name="Interpretation"
                      error={errors.Interpretation}
                      value={data.Interpretation["@value"]}
                      onChange={handleChange}
                      identical={compare("Interpretation")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
                <h5>Clinical Observations/Family Clinical Summary:</h5>
                <FormTextArea
                  name="ClinicalSummary"
                  error={errors.ClinicalSummary}
                  value={data.ClinicalSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalSummary")}
                  commentsHandler={handleComments}
                  disabled
                />
                <FormTextArea
                  name="ClinicalSummaryComments"
                  label="Additional comments:"
                  error={errors.ClinicalSummaryComments}
                  value={data.ClinicalSummaryComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ClinicalSummaryComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div title="Concerns include (Safety, supervision, parenting skills, medical, mental health, behavioral, family relations, financial, employment, education, housing/home environment, caregiver issues, hygiene, safe sleep, substance use)">
                  <h5>
                    Areas of Concern/Risk Factors/Potential Barriers:{" "}
                    <i className="fas fa-info-circle"></i>
                  </h5>
                </div>
                <FormTextArea
                  name="RiskFactors"
                  error={errors.RiskFactors}
                  value={data.RiskFactors["@value"]}
                  onChange={handleChange}
                  identical={compare("RiskFactors")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Concrete Needs:</h5>
                <FormTextArea
                  name="ConcreteNeeds"
                  error={errors.ConcreteNeeds}
                  value={data.ConcreteNeeds["@value"]}
                  onChange={handleChange}
                  identical={compare("ConcreteNeeds")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Family's Perspective/Plan for Discharge:</h5>
                <FormTextArea
                  name="DischargePlan"
                  error={errors.DischargePlan}
                  value={data.DischargePlan["@value"]}
                  onChange={handleChange}
                  identical={compare("DischargePlan")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="Diagnosis"
                expandStatus={expandStatus}
                className="mt-2"
                title="Diagnosis/Evaluation"
              >
                <FieldTable
                  name="DiagnosisDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">Family Member</div>
                      <div className="col-9 border-end py-2">
                        ICD-10 Code and Classification
                      </div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveFieldTableRow(
                                        data,
                                        setData,
                                        index,
                                        "DiagnosisDetails",
                                        buildClinicalNarrativeSummary
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteFieldTableRow(
                                        data,
                                        setData,
                                        index,
                                        "DiagnosisDetails",
                                        buildClinicalNarrativeSummary
                                      );
                                    }}
                                  ></i>
                                  <FormInput
                                    name="FamilyMember"
                                    value={item.FamilyMember}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditFieldTableRow(
                                        data,
                                        setData,
                                        index,
                                        "DiagnosisDetails",
                                        buildClinicalNarrativeSummary
                                      );
                                    }}
                                  ></i>
                                  {item.FamilyMember}
                                </>
                              )}
                            </div>
                            <div className="col-9 py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <FormSelect
                                    name="Code"
                                    value={item.Code}
                                    options={diagnosisOptions}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  {item.Code === "Other" && (
                                    <FormInput
                                      name="OtherCode"
                                      value={item.OtherCode}
                                      onChange={(e) => {
                                        handleFieldTableChange(
                                          data,
                                          e,
                                          index,
                                          "DiagnosisDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  )}
                                </>
                              ) : item.OtherCode !== undefined &&
                                item.OtherCode !== "" ? (
                                item.OtherCode
                              ) : (
                                item.Code
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddFieldTableRow(
                          data,
                          setData,
                          "DiagnosisDetails",
                          {
                            IsEditable: true,
                          }
                        );
                      }}
                    >
                      Add Diagnosis
                    </button>
                  ) : null}
                  {errors.DiagnosisDetails !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DiagnosisDetails}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                <FieldTable
                  name="TreatmentRecommendations"
                  commentsHandler={handleComments}
                >
                  <b>Treatment Recommendations</b>
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Service Type</div>
                      <div className="col border-end py-2">
                        Service Recipient
                      </div>
                      <div className="col border-end py-2">
                        Target Treatment Goal(s)
                      </div>
                      <div className="col py-2">
                        <div title="Concerns include (Safety, supervision, parenting skills, medical, mental health, behavioral, family relations, financial, employment, education, housing/home environment, caregiver issues, hygiene, safe sleep, substance use)">
                          Intervention(s) &amp; Service Delivery{" "}
                          <i>(Frequency/Duration of Service, Modality)</i>
                        </div>
                      </div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveFieldTableRow(
                                        data,
                                        setData,
                                        index,
                                        "TreatmentRecommendations",
                                        buildClinicalNarrativeSummary
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteFieldTableRow(
                                        data,
                                        setData,
                                        index,
                                        "TreatmentRecommendations",
                                        buildClinicalNarrativeSummary
                                      );
                                    }}
                                  ></i>
                                  <FormSelect
                                    name="ServiceType"
                                    value={item.ServiceType}
                                    options={serviceTypeOptions}
                                    onChange={(e) => {
                                      handleFieldTableChange(
                                        data,
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditFieldTableRow(
                                        data,
                                        setData,
                                        index,
                                        "TreatmentRecommendations",
                                        buildClinicalNarrativeSummary
                                      );
                                    }}
                                  ></i>
                                  {item.ServiceType}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Recipient"
                                  value={item.Recipient}
                                  onChange={(e) => {
                                    handleFieldTableChange(
                                      data,
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Recipient
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Goals"
                                  value={item.Goals}
                                  onChange={(e) => {
                                    handleFieldTableChange(
                                      data,
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Goals
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <FormInput
                                  name="Interventions"
                                  value={item.Interventions}
                                  onChange={(e) => {
                                    handleFieldTableChange(
                                      data,
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Interventions
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddFieldTableRow(
                            data,
                            setData,
                            "TreatmentRecommendations",
                            {
                              IsEditable: true,
                            }
                          );
                        }}
                      >
                        Add Treatment Recommendation
                      </button>
                    </>
                  )}
                  {errors.TreatmentRecommendations !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TreatmentRecommendations}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <h5>Additional Evaluations Needed:</h5>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalPsychiatric"
                      label="Psychiatric"
                      checked={data.AdditionalPsychiatric["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalPsychological"
                      label="Psychological"
                      checked={
                        data.AdditionalPsychological["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalNeurological"
                      label="Neurological"
                      checked={data.AdditionalNeurological["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalEducational"
                      label="Educational"
                      checked={data.AdditionalEducational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalMedical"
                      label="Medical"
                      checked={data.AdditionalMedical["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalDental"
                      label="Dental"
                      checked={data.AdditionalDental["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalSubstanceAbuse"
                      label="Substance Abuse"
                      checked={
                        data.AdditionalSubstanceAbuse["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalVocational"
                      label="Vocational"
                      checked={data.AdditionalVocational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalOccupational"
                      label="Occupational"
                      checked={data.AdditionalOccupational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalNutritional"
                      label="Nutritional"
                      checked={data.AdditionalNutritional["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalVisualAuditory"
                      label="Visual/Auditory"
                      checked={
                        data.AdditionalVisualAuditory["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalDomesticViolence"
                      label="Domestic Violence"
                      checked={
                        data.AdditionalDomesticViolence["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalSexOffender"
                      label="Sexual Offender"
                      checked={data.AdditionalSexOffender["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      name="AdditionalOther"
                      label={`Other${
                        data.AdditionalOther["@value"] === "true" ? ":" : ""
                      }`}
                      checked={data.AdditionalOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.AdditionalOther["@value"] === "true" && (
                      <FormInput
                        name="AdditionalOtherDetails"
                        error={errors.AdditionalOtherDetails}
                        value={data.AdditionalOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("AdditionalOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col"></div>
                  <div className="col"></div>
                </div>
                {data.AdditionalPsychiatric["@value"] === "true" ||
                data.AdditionalPsychological["@value"] === "true" ||
                data.AdditionalNeurological["@value"] === "true" ||
                data.AdditionalEducational["@value"] === "true" ||
                data.AdditionalMedical["@value"] === "true" ||
                data.AdditionalDental["@value"] === "true" ||
                data.AdditionalSubstanceAbuse["@value"] === "true" ||
                data.AdditionalVocational["@value"] === "true" ||
                data.AdditionalOccupational["@value"] === "true" ||
                data.AdditionalNutritional["@value"] === "true" ||
                data.AdditionalVisualAuditory["@value"] === "true" ||
                data.AdditionalDomesticViolence["@value"] === "true" ||
                data.AdditionalSexOffender["@value"] === "true" ||
                data.AdditionalOther["@value"] === "true" ? (
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="MembersInNeed"
                        label="Family Member(s) in need of evaluation:"
                        error={errors.MembersInNeed}
                        value={data.MembersInNeed["@value"]}
                        onChange={handleChange}
                        identical={compare("MembersInNeed")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                ) : null}
              </ExpandDiv>
              {showNarrative === "Show" && (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Observations/Family Clinical Summary:</b>
                  </label>
                  <textarea
                    value={data.ClinicalSummary["@value"]}
                    rows="5"
                    disabled
                  />
                </div>
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare && (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default INEFPSD;
